import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Target, CheckCircle2, AlertTriangle, XCircle, Clock, TrendingUp, Server, FolderTree, Download } from 'lucide-react';
import { exportToCSV } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function AvailabilityBar({ value, target = 99.9 }) {
  const getColor = () => {
    if (value >= target) return 'bg-green-500';
    if (value >= 99) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className={value >= target ? 'text-green-600' : value >= 99 ? 'text-yellow-600' : 'text-red-600'}>
          {value.toFixed(2)}%
        </span>
        <span className="text-muted-foreground">Meta: {target}%</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${getColor()} transition-all`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}

function SLACard({ name, availability, hostsCount, downtime, status }) {
  const getStatusIcon = () => {
    if (status === 'ok') return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    if (status === 'warning') return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusBg = () => {
    if (status === 'ok') return 'bg-green-50 border-green-200';
    if (status === 'warning') return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <Card className={`${getStatusBg()} border`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate" title={name}>{name}</h3>
            <p className="text-xs text-muted-foreground">{hostsCount} hosts</p>
          </div>
          {getStatusIcon()}
        </div>
        <AvailabilityBar value={availability} />
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Downtime: {downtime}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryCard({ title, value, subtitle, icon: Icon, variant }) {
  const getBg = () => {
    if (variant === 'success') return 'bg-green-50 border-green-200';
    if (variant === 'warning') return 'bg-yellow-50 border-yellow-200';
    if (variant === 'error') return 'bg-red-50 border-red-200';
    return 'bg-muted';
  };

  const getIconColor = () => {
    if (variant === 'success') return 'text-green-500';
    if (variant === 'warning') return 'text-yellow-500';
    if (variant === 'error') return 'text-red-500';
    return 'text-muted-foreground';
  };

  return (
    <Card className={getBg()}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <Icon className={`h-8 w-8 ${getIconColor()}`} />
        </div>
      </CardContent>
    </Card>
  );
}

export function AvailabilityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [searchTerm, setSearchTerm] = useState('');
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(API + '/availability?days=' + period, { headers: getAuthHeader() });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary = data?.summary || {};
  const byCategory = data?.by_category || [];
  const byGroup = data?.by_group || [];

  // Filter data
  const filteredCategories = byCategory.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredGroups = byGroup.filter(g => 
    g.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const categoriesOk = byCategory.filter(c => c.sla_status === 'ok').length;
  const categoriesWarning = byCategory.filter(c => c.sla_status === 'warning').length;
  const categoriesCritical = byCategory.filter(c => c.sla_status === 'critical').length;

  const groupsOk = byGroup.filter(g => g.sla_status === 'ok').length;
  const groupsWarning = byGroup.filter(g => g.sla_status === 'warning').length;
  const groupsCritical = byGroup.filter(g => g.sla_status === 'critical').length;

  const handleExportCategoriesCSV = () => {
    const columns = [
      { key: 'name', label: 'Categoria' },
      { key: 'hosts_count', label: 'Hosts' },
      { key: 'availability', label: 'Disponibilidade (%)' },
      { key: 'downtime_str', label: 'Downtime' },
      { key: 'sla_status', label: 'Status SLA' }
    ];
    exportToCSV(filteredCategories, columns, `disponibilidade_categorias_${period}d`);
  };

  const handleExportGroupsCSV = () => {
    const columns = [
      { key: 'name', label: 'Grupo' },
      { key: 'hosts_count', label: 'Hosts' },
      { key: 'availability', label: 'Disponibilidade (%)' },
      { key: 'downtime_str', label: 'Downtime' },
      { key: 'sla_status', label: 'Status SLA' }
    ];
    exportToCSV(filteredGroups, columns, `disponibilidade_grupos_${period}d`);
  };

  return (
    <div className="space-y-6" data-testid="availability-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Disponibilidade SLA / SLO</h2>
          <p className="text-muted-foreground">Visão geral de disponibilidade por categoria e grupo</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="14">Últimos 14 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="60">Últimos 60 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard 
          title="Disponibilidade Geral" 
          value={`${summary.overall_availability || 100}%`}
          subtitle={`${summary.total_hosts || 0} hosts monitorados`}
          icon={Target}
          variant={summary.overall_availability >= 99.9 ? 'success' : summary.overall_availability >= 99 ? 'warning' : 'error'}
        />
        <SummaryCard 
          title="Dentro do SLA" 
          value={categoriesOk + groupsOk}
          subtitle="Categorias e grupos"
          icon={CheckCircle2}
          variant="success"
        />
        <SummaryCard 
          title="Atenção" 
          value={categoriesWarning + groupsWarning}
          subtitle="Abaixo de 99.9%"
          icon={AlertTriangle}
          variant="warning"
        />
        <SummaryCard 
          title="Crítico" 
          value={categoriesCritical + groupsCritical}
          subtitle="Abaixo de 99%"
          icon={XCircle}
          variant="error"
        />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar categoria ou grupo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <Tabs defaultValue="category" className="space-y-4">
          <TabsList>
            <TabsTrigger value="category" className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              Por Categoria ({byCategory.length})
            </TabsTrigger>
            <TabsTrigger value="group" className="flex items-center gap-2">
              <FolderTree className="h-4 w-4" />
              Por Grupo ({byGroup.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="category" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />{categoriesOk} OK
                </Badge>
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                  <AlertTriangle className="h-3 w-3 mr-1" />{categoriesWarning} Atenção
                </Badge>
                <Badge variant="secondary" className="bg-red-100 text-red-700">
                  <XCircle className="h-3 w-3 mr-1" />{categoriesCritical} Crítico
                </Badge>
              </div>
              <Button variant="outline" size="sm" onClick={handleExportCategoriesCSV}>
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredCategories.map(cat => (
                <SLACard
                  key={cat.name}
                  name={cat.name}
                  availability={cat.availability}
                  hostsCount={cat.hosts_count}
                  downtime={cat.downtime_str}
                  status={cat.sla_status}
                />
              ))}
              {filteredCategories.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  <Server className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  Nenhuma categoria encontrada
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="group" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />{groupsOk} OK
                </Badge>
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                  <AlertTriangle className="h-3 w-3 mr-1" />{groupsWarning} Atenção
                </Badge>
                <Badge variant="secondary" className="bg-red-100 text-red-700">
                  <XCircle className="h-3 w-3 mr-1" />{groupsCritical} Crítico
                </Badge>
              </div>
              <Button variant="outline" size="sm" onClick={handleExportGroupsCSV}>
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            </div>

            <ScrollArea className="h-[600px]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pr-4">
                {filteredGroups.map(group => (
                  <SLACard
                    key={group.name}
                    name={group.name}
                    availability={group.availability}
                    hostsCount={group.hosts_count}
                    downtime={group.downtime_str}
                    status={group.sla_status}
                  />
                ))}
                {filteredGroups.length === 0 && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    <FolderTree className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    Nenhum grupo encontrado
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
